import React, { useMemo, useState } from "react";

function computeLayout(nodes, edges) {
  const adj = {};
  const indeg = {};
  nodes.forEach((n) => { indeg[n.id] = 0; });
  edges.forEach((e) => {
    (adj[e.source] = adj[e.source] || []).push(e.target);
    if (indeg[e.target] !== undefined) indeg[e.target] += 1;
  });
  const layer = {};
  let starts = nodes.filter((n) => n.is_start).map((n) => n.id);
  if (!starts.length) starts = nodes.filter((n) => indeg[n.id] === 0).map((n) => n.id);
  if (!starts.length && nodes.length) starts = [nodes[0].id];
  const dfs = (id, depth, seen) => {
    if (layer[id] === undefined || depth > layer[id]) layer[id] = depth;
    if (seen.has(id) || depth > nodes.length + 2) return;
    const ns = new Set(seen); ns.add(id);
    (adj[id] || []).forEach((t) => dfs(t, depth + 1, ns));
  };
  starts.forEach((s) => dfs(s, 0, new Set()));
  nodes.forEach((n) => { if (layer[n.id] === undefined) layer[n.id] = 0; });

  const cols = {};
  nodes.forEach((n) => { (cols[layer[n.id]] = cols[layer[n.id]] || []).push(n); });
  const colW = 210, rowH = 92, nodeW = 158, nodeH = 56;
  const pos = {};
  const maxLayer = Math.max(...Object.keys(cols).map(Number));
  const maxRows = Math.max(...Object.values(cols).map((c) => c.length));
  Object.entries(cols).forEach(([l, list]) => {
    list.forEach((n, i) => {
      const colCount = list.length;
      const yOffset = (maxRows - colCount) * rowH / 2;
      pos[n.id] = {
        x: Number(l) * colW + 30,
        y: i * rowH + 40 + yOffset,
        w: nodeW, h: nodeH,
      };
    });
  });
  const width = (maxLayer + 1) * colW + 60;
  const height = maxRows * rowH + 80;
  return { pos, width, height };
}

export default function ProcessMap({ data, metric = "frequency" }) {
  const [hover, setHover] = useState(null);
  const { nodes, edges } = data || { nodes: [], edges: [] };
  const { pos, width, height } = useMemo(() => computeLayout(nodes, edges), [nodes, edges]);
  const maxFreq = Math.max(...edges.map((e) => e.frequency), 1);
  const maxNodeFreq = Math.max(...nodes.map((n) => n.frequency), 1);

  if (!nodes.length) return null;

  return (
    <div className="overflow-auto rounded-2xl bg-gradient-to-br from-slate-50 to-cyan-50/40 border border-slate-200 p-2" data-testid="process-map-canvas">
      <svg width={width} height={height} style={{ minWidth: "100%" }}>
        <defs>
          <marker id="pm-arrow" markerWidth="10" markerHeight="10" refX="8" refY="3" orient="auto">
            <path d="M0,0 L0,6 L8,3 z" fill="#0e7490" />
          </marker>
          <linearGradient id="pm-node" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#f0f9ff" />
          </linearGradient>
        </defs>
        {edges.map((e, i) => {
          const s = pos[e.source], t = pos[e.target];
          if (!s || !t) return null;
          const x1 = s.x + s.w, y1 = s.y + s.h / 2;
          const x2 = t.x, y2 = t.y + t.h / 2;
          const mx = (x1 + x2) / 2;
          const thickness = 1 + (e.frequency / maxFreq) * 5;
          const isHot = metric === "performance" && e.avg_duration_hours > 40;
          const active = hover && (hover === e.source || hover === e.target);
          const label = metric === "performance"
            ? (e.avg_duration_hours < 48 ? `${e.avg_duration_hours}h` : `${(e.avg_duration_hours / 24).toFixed(1)}d`)
            : e.frequency;
          return (
            <g key={i} opacity={hover && !active ? 0.25 : 1} style={{ transition: "opacity 0.2s" }}>
              <path d={`M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`} fill="none"
                stroke={isHot ? "#f59e0b" : "#0e7490"} strokeWidth={thickness}
                strokeOpacity={0.55} markerEnd="url(#pm-arrow)"
                className={e.frequency / maxFreq > 0.5 ? "pm-flow-edge" : ""} />
              <g transform={`translate(${mx - 16}, ${(y1 + y2) / 2 - 9})`}>
                <rect width="34" height="18" rx="9" fill={isHot ? "#fef3c7" : "#ffffff"} stroke={isHot ? "#f59e0b" : "#cbd5e1"} />
                <text x="17" y="13" textAnchor="middle" fontSize="9" fontWeight="600" fill={isHot ? "#b45309" : "#0e7490"}>{label}</text>
              </g>
            </g>
          );
        })}
        {nodes.map((n) => {
          const p = pos[n.id];
          if (!p) return null;
          const intensity = n.frequency / maxNodeFreq;
          return (
            <g key={n.id} transform={`translate(${p.x},${p.y})`} onMouseEnter={() => setHover(n.id)} onMouseLeave={() => setHover(null)}
              style={{ cursor: "pointer" }} data-testid={`pm-node-${n.id}`}>
              <rect width={p.w} height={p.h} rx="14" fill="url(#pm-node)"
                stroke={n.is_start ? "#10b981" : n.is_end ? "#6366f1" : "#0e7490"}
                strokeWidth={hover === n.id ? 2.5 : 1.5} strokeOpacity={0.7}
                style={{ filter: "drop-shadow(0 4px 10px rgba(14,116,144,0.10))" }} />
              <rect width="4" height={p.h} rx="2" fill={n.is_start ? "#10b981" : n.is_end ? "#6366f1" : "#0e7490"} fillOpacity={0.3 + intensity * 0.7} />
              <text x="14" y="23" fontSize="11.5" fontWeight="700" fill="#0f172a">
                {n.label.length > 20 ? n.label.slice(0, 19) + "…" : n.label}
              </text>
              <text x="14" y="42" fontSize="10" fill="#64748b">{`${n.frequency} events · ${n.case_count} cases`}</text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
